import React, { useState, useMemo, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { getFileTypes } from 'ducks/files/selectors'
import { selectFileCategories } from 'ducks/dictionaries/selectors'
import { isNilOrEmpty } from 'utils/ramda'
import SearchIcon from '@mui/icons-material/Search'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FolderIcon from '@mui/icons-material/Folder'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'

const EnhancedFileTypeSelector = ({
  value,
  onSelect,
  hasError,
  placeholder = 'Select file type...',
  autoFocus = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const fileTypes = useSelector(getFileTypes)
  const fileCategories = useSelector(selectFileCategories)
  const containerRef = useRef(null)

  const folderColors = {
    'Plans & Drawings': '#4CAF50',
    'Certificates & Reports': '#33becc',
    'Building Information': '#FF9800',
    'Legal & Compliance': '#9C27B0',
    'Emergency Response Pack (ERP)': '#F44336',
    'Maintenance & Operations': '#607D8B',
    'Energy & Sustainability': '#8BC34A',
    'Safety & Security': '#FF5722',
    Financial: '#3F51B5',
    Other: '#757575'
  }

  const selectedFileType = useMemo(() => {
    return fileTypes.find(type => type.id === value)
  }, [value, fileTypes])

  const selectedCategory = useMemo(() => {
    if (selectedFileType) {
      return fileCategories.find(
        cat => cat.id === selectedFileType.fileCategoryId
      )
    }
    return null
  }, [selectedFileType, fileCategories])

  const groupedFileTypes = useMemo(() => {
    const grouped = {}

    fileCategories
      .filter(cat => cat.name !== 'FRS Information Exchange')
      .forEach(category => {
        const categoryTypes = fileTypes.filter(
          type =>
            type.fileCategoryId === category.id &&
            (isNilOrEmpty(searchTerm) ||
              type.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              category.name.toLowerCase().includes(searchTerm.toLowerCase()))
        )

        if (categoryTypes.length > 0) {
          grouped[category.id] = {
            category,
            types: categoryTypes
          }
        }
      })

    return grouped
  }, [fileCategories, fileTypes, searchTerm])

  useEffect(() => {
    const handleClickOutside = event => {
      // Check if click is outside both the container and the dropdown
      const isClickOutsideContainer = containerRef.current && !containerRef.current.contains(event.target)
      const isClickOutsideDropdown = !event.target.closest('.enhanced-dropdown')

      if (isClickOutsideContainer && isClickOutsideDropdown) {
        event.stopPropagation()
        setIsOpen(false)
        setSearchTerm('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside, true)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside, true)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    updateDropdownPosition()

    const handleReposition = () => updateDropdownPosition()
    window.addEventListener('resize', handleReposition)
    window.addEventListener('scroll', handleReposition, true)

    return () => {
      window.removeEventListener('resize', handleReposition)
      window.removeEventListener('scroll', handleReposition, true)
    }
  }, [isOpen])

  useEffect(() => {
    if (typeof document === 'undefined') return

    if (isOpen) {
      document.body.dataset.fileTypeDropdownOpen = 'true'
    } else {
      delete document.body.dataset.fileTypeDropdownOpen
    }

    return () => {
      delete document.body.dataset.fileTypeDropdownOpen
    }
  }, [isOpen])

  // Handle autoFocus prop
  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        handleToggle()
      }, 300)
    }
  }, [autoFocus])

  const handleFileTypeSelect = (categoryId, typeId) => {
    onSelect(categoryId, typeId)
    setIsOpen(false)
    setSearchTerm('')
  }

  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      const spaceBelow = viewportHeight - rect.bottom
      const spaceAbove = rect.top
      const dropdownHeight = 400

      const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

      const width = Math.max(rect.width, 260)
      const maxLeft = Math.max(8, viewportWidth - width - 8)
      const left = Math.min(Math.max(rect.left, 8), maxLeft)

      setDropdownPosition({
        top: showAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
        left,
        width
      })
    }
  }

  const handleToggle = () => {
    if (!isOpen) {
      updateDropdownPosition()
    }
    setIsOpen(!isOpen)
    if (!isOpen) {
      setTimeout(() => {
        const searchInput = document.querySelector('.enhanced-dropdown input')
        searchInput?.focus()
      }, 100)
    }
  }

  return (
    <Container ref={containerRef} className='enhanced-file-type-selector'>
      <SelectButton onClick={handleToggle} hasError={hasError} isOpen={isOpen}>
        <SelectedValue>
          {selectedFileType ? (
            <SelectedItem>
              <FolderIconStyled
                color={folderColors[selectedCategory?.name] || '#757575'}
              />
              <SelectedText>
                <CategoryName>{selectedCategory?.name}</CategoryName>
                <TypeName>{selectedFileType.name}</TypeName>
              </SelectedText>
            </SelectedItem>
          ) : (
            <PlaceholderText>{placeholder}</PlaceholderText>
          )}
        </SelectedValue>
        <ArrowIcon isOpen={isOpen} />
      </SelectButton>

      {isOpen && typeof document !== 'undefined' && createPortal(
        <>
          <Overlay onClick={() => {
            setIsOpen(false)
            setSearchTerm('')
          }} />
          <Dropdown
            className="enhanced-dropdown"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width
            }}
          >
            <SearchWrapper>
              <SearchIconStyled />
              <SearchInput
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder='Search file types...'
                autoFocus
              />
            </SearchWrapper>

            <OptionsContainer>
              {Object.keys(groupedFileTypes).length === 0 ? (
                <NoResults>No file types found</NoResults>
              ) : (
                Object.values(groupedFileTypes).map(({ category, types }) => (
                  <CategoryGroup key={category.id}>
                    <CategoryHeader>
                      <FolderIconStyled
                        color={folderColors[category.name] || '#757575'}
                      />
                      <CategoryTitle>{category.name}</CategoryTitle>
                      <TypeCount>{types.length}</TypeCount>
                    </CategoryHeader>
                    <TypesList>
                      {types.map(type => (
                        <TypeOption
                          key={type.id}
                          onClick={() =>
                            handleFileTypeSelect(category.id, type.id)
                          }
                          isSelected={type.id === value}
                        >
                          <FileIconStyled />
                          <TypeOptionText>
                            <TypeOptionName>{type.name}</TypeOptionName>
                            {type.description && (
                              <TypeOptionDescription>
                                {type.description}
                              </TypeOptionDescription>
                            )}
                          </TypeOptionText>
                        </TypeOption>
                      ))}
                    </TypesList>
                  </CategoryGroup>
                ))
              )}
            </OptionsContainer>
          </Dropdown>
        </>,
        document.body
      )}
    </Container>
  )
}

export default EnhancedFileTypeSelector

const Container = styled.div`
  position: relative;
  width: 100%;
`

const SelectButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid ${({ theme, hasError, isOpen }) =>
    hasError ? theme.colors.error : isOpen ? '#34bdcc' : theme.colors.grey[500]};
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 48px;
  box-sizing: border-box;

  &:hover {
    border-color: #34bdcc;
  }

  ${({ isOpen }) =>
    isOpen &&
    `
    border-color: #34bdcc;
    box-shadow: 0 0 0 3px rgba(52, 189, 204, 0.15);
  `}
`

const SelectedValue = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
`

const SelectedItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-width: 0;
`

const SelectedText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

const CategoryName = styled.div`
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`

const TypeName = styled.div`
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PlaceholderText = styled.div`
  color: #9ca3af;
  font-size: 14px;
`

const ArrowIcon = styled(KeyboardArrowDownIcon)`
  transition: transform 0.2s ease;
  color: #6b7280;
  flex-shrink: 0;

  ${({ isOpen }) =>
    isOpen &&
    `
    transform: rotate(180deg);
  `}
`

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.15);
  z-index: 2147483648;
  animation: fadeIn 0.15s ease;
  display: none;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (max-width: 768px) {
    display: block;
  }
`

const Dropdown = styled.div`
  position: fixed;
  z-index: 2147483649;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2), 0 8px 16px rgba(0, 0, 0, 0.1);
  max-height: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: dropdownSlide 0.2s ease;

  @keyframes dropdownSlide {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    position: fixed !important;
    top: auto !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    width: 100% !important;
    max-height: 70vh;
    border-radius: 20px 20px 0 0;
    animation: slideUp 0.3s ease;

    @keyframes slideUp {
      from {
        transform: translateY(100%);
      }
      to {
        transform: translateY(0);
      }
    }
  }
`

const SearchWrapper = styled.div`
  position: relative;
  padding: 16px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
  background: white;

  @media (max-width: 768px) {
    padding: 16px;
    padding-top: 20px;

    &::before {
      content: '';
      position: absolute;
      top: 8px;
      left: 50%;
      transform: translateX(-50%);
      width: 36px;
      height: 4px;
      background: #d1d5db;
      border-radius: 2px;
    }
  }
`

const SearchIconStyled = styled(SearchIcon)`
  position: absolute;
  left: 28px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 20px !important;

  @media (max-width: 768px) {
    left: 28px;
    top: calc(50% + 4px);
  }
`

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  background: #f9fafb;
  transition: all 0.2s;

  &:focus {
    border-color: #34bdcc;
    background: white;
    box-shadow: 0 0 0 3px rgba(52, 189, 204, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }

  @media (max-width: 768px) {
    padding: 14px 14px 14px 44px;
    font-size: 16px; /* Previne zoom pe iOS */
  }
`

const OptionsContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  background: white;
  -webkit-overflow-scrolling: touch;
`

const NoResults = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`

const CategoryGroup = styled.div`
  border-bottom: 1px solid #f3f4f6;

  &:last-child {
    border-bottom: none;
  }
`

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f9fafb;
  font-weight: 600;
  font-size: 13px;
  color: #374151;
  position: sticky;
  top: 0;
  z-index: 1;
  border-bottom: 1px solid #f3f4f6;
`

const CategoryTitle = styled.div`
  flex: 1;
`

const TypeCount = styled.div`
  background: #e5e7eb;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
`

const TypesList = styled.div`
  background: white;
`

const TypeOption = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px 12px 24px;
  cursor: pointer;
  transition: all 0.15s ease;
  border-left: 3px solid transparent;
  background: white;

  &:hover {
    background: #f0fdfa;
    border-left-color: #34bdcc;
  }

  ${({ isSelected }) =>
    isSelected &&
    `
    background: #f0fdfa;
    border-left-color: #34bdcc;
  `}

  @media (max-width: 768px) {
  padding: 14px 16px 14px 20px;
}
`

const TypeOptionText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`

const TypeOptionName = styled.div`
  font-size: 14px;
  color: #1f2937;
  font-weight: 500;
  line-height: 1.3;
`

const TypeOptionDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  line-height: 1.4;
`

const FolderIconStyled = styled(FolderIcon)`
  font-size: 22px !important;
  color: ${({ color }) => color};
  flex-shrink: 0;
`

const FileIconStyled = styled(InsertDriveFileIcon)`
  font-size: 18px !important;
  color: #9ca3af;
  flex-shrink: 0;
  margin-top: 2px;
`
